/* eslint-disable no-console */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useAsyncEffect } from 'use-async-effect';
import Tracking from 'libs/tracking';
import { AdvisorCard, AdvisorCardSkeleton } from 'components';
import {
  ADVISOR_AVAILABILITY,
  CALL_FLOW_SOURCE,
  CHAT_FLOW_SOURCE,
  PAGE_ID,
  CALL_AVAILABILITY_PROP,
  CHAT_AVAILABILITY_PROP,
  EVENT_ACTION_TYPES
} from 'libs/constants';
import {
  useExperimentationAllocationMutation,
  useLazyGetListingsQuery
} from 'store/api';
import { AuthSession } from 'hooks/useAuth';
import { HomePageContext } from 'pages/index.page';

import {
  AdvisorStatusInfo,
  useAdvisorAvailability
} from 'hooks/useAdvisorAvailability';
import Pubnub from 'pubnub';
import { AdvisorsWrapper } from './styled';

function Advisors(): JSX.Element {
  const [advisorListings, setAdvisorListings] = useState([]);
  const advisorIds = useMemo(
    () => [...advisorListings].map((l) => l.advisorId).sort(),
    [advisorListings]
  );
  const [refetchListings, setRefetchListings] = useState<boolean>(true);
  const [pubnubClient, setPubnubClient] = useState<Pubnub>();

  const auth = AuthSession();
  const [getExperimentationProp, { data: mergedListingProperties }] =
    useExperimentationAllocationMutation();
  const [getListings] = useLazyGetListingsQuery({
    pollingInterval: 30000
  });
  const { keenTempUid } = useContext(HomePageContext);
  const { takingCalls } = ADVISOR_AVAILABILITY.callAvailability;
  const { takingChat } = ADVISOR_AVAILABILITY.chatAvailability;
  const onCallButtonClick = (callAvailability) => {
    if (callAvailability === takingCalls)
      Tracking.track('Call Now Button Clicked', {}, true);
  };
  const onChatButtonClick = (chatAvailability) => {
    if (chatAvailability === takingChat)
      Tracking.track('Chat Now Button Clicked', {}, true);
  };

  const captureAdvisorListingImpression = (
    type: number,
    listingId: number,
    advisorId: string | number,
    position: number,
    callAvailability: number,
    chatAvailability: number
  ) => {
    const props = {
      listing_id: listingId,
      advisor_id: advisorId,
      position,
      listings_per_page: advisorListings.length,
      availability_call: CALL_AVAILABILITY_PROP[callAvailability],
      availability_chat: CHAT_AVAILABILITY_PROP[chatAvailability],
      platform: 'web',
      screen: 'homepage'
    };
    if (type === EVENT_ACTION_TYPES.clicked)
      Tracking.track('Advisor Listing Clicked', props, true);
    else if (type === EVENT_ACTION_TYPES.viewed) {
      const viewedProps = { ...props, randomization_type: 'none' };
      Tracking.track('Advisor Listing Viewed', viewedProps, false);
    }
  };

  const mergedAvailableSorts =
    mergedListingProperties && mergedListingProperties.availableSorts;
  const mergedSortOverrides =
    mergedListingProperties && mergedListingProperties.sortOverrides;
  const getSortId = useCallback(() => {
    const sortId = 14; // Featured Advisor
    const matchingOverride = mergedSortOverrides.find(
      (srt) => srt.CurrentSortId === sortId
    );
    if (matchingOverride != null) {
      return matchingOverride.CurrentSortId;
    }
    const availableSorts = mergedAvailableSorts.filter((x) =>
      x.overrideSortIds.includes(sortId)
    );
    if (availableSorts != null && availableSorts.length > 0) {
      return availableSorts[0].sortId;
    }
    return sortId;
  }, [mergedAvailableSorts, mergedSortOverrides]);

  useEffect(() => {
    if (auth.isAuthenticated !== undefined)
      getExperimentationProp({ userId: auth.user.id, keenTempUid });
  }, [getExperimentationProp, auth.isAuthenticated, auth.user.id, keenTempUid]);

  const onAdvisorStatusChange = useCallback(
    async (status: AdvisorStatusInfo) => {
      const { advisorId } = status;
      console.log(`🚥 Advisor Status for ${advisorId}`, status);

      if (status.advisorIsAway || status.advisorIsBusy) {
        console.log(
          `Will refresh listings... Advisor [id: ${advisorId}] is busy or away!`
        );
        if (pubnubClient) {
          await pubnubClient.unsubscribeAll();
          console.info('🧹 Unsubscribing all channels from pubnub client...');
        }

        setRefetchListings(() => true); // triggers refetch of advisor listings
        return;
      }

      try {
        setAdvisorListings((listings) => {
          const newAdvisorListings = listings.map((l) => ({ ...l }));

          const listing = newAdvisorListings.find(
            (l) => l.advisorId === advisorId
          );
          if (!listing) return listings;
          listing.chatAvailability = status.chatAvailabilityId;
          listing.majorRank = status.callMajorRank;
          listing.availability = status.callAvailabilityId;
          // Since we are mutating listing data in the pre-fetched listings data (obtained via polling) to update realtime status, we set a flag to prevent refetching.
          setRefetchListings(() => false);
          return newAdvisorListings;
        });
      } catch (error) {
        console.log('error updating listing data', error);
      }
    },
    [pubnubClient]
  );

  // Use useAdvisorAvailability hook to listen for realtime advisor listing updates.
  useAdvisorAvailability(
    advisorIds,
    // on intialization of pubnub client
    (initializedPubnubClient) => {
      setPubnubClient(() => initializedPubnubClient);
    },
    // on availability change (when pubnub message is recieved)
    (statusInfo) => {
      onAdvisorStatusChange(statusInfo);
    },
    // on error
    (error) => {
      console.error('Availability Listener Error: ', error);
    }
  );

  useAsyncEffect(async () => {
    // do not refetch if listing data is mutated via pubnub messages
    if (!refetchListings) {
      return;
    }

    if (pubnubClient) {
      await pubnubClient.unsubscribeAll();
      console.info('🧹 Advisors: Unsubscribing all channels...');
    }
    if (mergedListingProperties) {
      const { data } = await getListings(getSortId().toString());
      setAdvisorListings(data);
    }
  }, [mergedListingProperties, getListings, getSortId, refetchListings]);

  return (
    <AdvisorsWrapper>
      {advisorListings && advisorListings.length > 0
        ? advisorListings.map((item, index) => (
            <AdvisorCard
              key={item.id}
              advisorInfo={item}
              pageId={PAGE_ID.homePage}
              gid={0}
              pmode={false}
              webTrack={CALL_FLOW_SOURCE.WebHomePageCall}
              chatTrackingSource={CHAT_FLOW_SOURCE.WebHomePageChat}
              flBidId={null}
              eligibleForFeaturedListingClicks
              appointmentTargetUri=""
              returnUrl="/MyAccount/Consumer"
              cancelUrl="/MyAccount/Consumer"
              flmode="call"
              onCallButtonClick={onCallButtonClick}
              onChatButtonClick={onChatButtonClick}
              captureAdvisorListingImpression={captureAdvisorListingImpression}
              position={index + 1}
            />
          ))
        : [...Array(3)].map((_, i) => <AdvisorCardSkeleton key={i} />)}
    </AdvisorsWrapper>
  );
}

export default Advisors;
